import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { leadAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { TrendingUp, Trophy, XCircle, Activity, ShieldAlert, BarChart3 } from 'lucide-react';
import { LEAD_STATUS } from './LeadsPage';

const STAGE_COLORS = {
  new: '#3b82f6', attempted: '#64748b', contacted: '#6366f1', qualified: '#8b5cf6',
  proposal: '#f59e0b', negotiation: '#f97316', won: '#10b981', lost: '#ef4444',
};

function StatCard({ icon: Icon, label, value, suffix, tone, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-heading font-semibold tabular-nums mt-0.5">{value}{suffix}</p>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon size={18} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function LeadAnalyticsPage() {
  const { canAccessLeads } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canAccessLeads) { setLoading(false); return; }
    leadAPI.analytics().then(r => setData(r.data)).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, [canAccessLeads]);

  if (!canAccessLeads) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center space-y-3" data-testid="analytics-no-access">
        <ShieldAlert size={40} className="mx-auto text-muted-foreground" />
        <h2 className="text-xl font-heading font-semibold">No access to Lead Management</h2>
        <p className="text-muted-foreground text-sm">Ask a super admin to grant you Lead Management access.</p>
      </div>
    );
  }

  if (loading) return <p className="text-muted-foreground py-12 text-center">Loading analytics...</p>;
  if (!data) return <p className="text-muted-foreground py-12 text-center">No analytics available.</p>;

  const funnelData = data.funnel.map(f => ({
    name: LEAD_STATUS[f.status]?.label || f.status, status: f.status, count: f.count,
  }));
  const campaignData = (data.by_campaign || []).map(c => ({
    name: c.key, leads: c.count, won: data.won_by_campaign?.[c.key] || 0,
  }));
  const sourceData = (data.by_source || []).map(s => ({ name: s.key, count: s.count }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-analytics-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight flex items-center gap-2">
          <BarChart3 size={22} className="text-primary" /> Lead Analytics
        </h1>
        <p className="text-muted-foreground">Conversion funnel & campaign performance</p>
      </div>

      {/* Stat cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Activity} label="Total leads" value={data.total} tone="bg-blue-100 text-blue-700" testid="stat-total" />
        <StatCard icon={Trophy} label="Won" value={data.won} tone="bg-emerald-100 text-emerald-700" testid="stat-won" />
        <StatCard icon={XCircle} label="Lost" value={data.lost} tone="bg-red-100 text-red-700" testid="stat-lost" />
        <StatCard icon={TrendingUp} label="Conversion rate" value={data.conversion_rate} suffix="%" tone="bg-violet-100 text-violet-700" testid="stat-conversion" />
      </div>

      {/* Funnel */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-heading">Pipeline funnel</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={funnelData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {funnelData.map((e) => <Cell key={e.status} fill={STAGE_COLORS[e.status] || '#3b82f6'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Campaigns: leads vs won */}
        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">Top campaigns (leads vs won)</CardTitle></CardHeader>
          <CardContent>
            {campaignData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No campaign data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={campaignData} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="leads" fill="#6366f1" radius={[4, 4, 0, 0]} name="Leads" />
                  <Bar dataKey="won" fill="#10b981" radius={[4, 4, 0, 0]} name="Won" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Sources */}
        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">Leads by source</CardTitle></CardHeader>
          <CardContent>
            {sourceData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No source data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={sourceData} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Leads" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
